<template>
    <!-- ================================================================
         TAB: Licenza e Fatturazione
         Da inserire come <v-window-item value="licenza"> in Profilo.vue
         ================================================================ -->
    <v-card flat class="tab-licenza">
        <v-card-text>

            <!-- Skeleton caricamento -->
            <template v-if="loading">
                <v-skeleton-loader type="article" />
            </template>

            <!-- Nessun abbonamento -->
            <template v-else-if="!abbonamento">
                <div class="empty-abb">
                    <v-icon size="48" color="grey-lighten-1">mdi-package-variant</v-icon>
                    <p class="text-body-1 font-weight-medium mt-3 mb-1">Nessun abbonamento attivo</p>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                        Invia una richiesta per attivare il tuo abbonamento.
                    </p>
                    <v-btn color="primary" prepend-icon="mdi-send-outline" @click="apriDialogRichiesta('nuovo')">
                        Richiedi abbonamento
                    </v-btn>
                </div>
            </template>

            <!-- Abbonamento presente -->
            <template v-else>

                <!-- Banner stato -->
                <v-alert v-if="abbonamento.stato === 'attesa_pagamento'" type="warning" variant="tonal" class="mb-4"
                    icon="mdi-clock-outline">
                    <strong>In attesa di pagamento.</strong>
                    Effettua il bonifico bancario e contatta il supporto per attivare l'abbonamento.
                </v-alert>
                <v-alert v-else-if="abbonamento.stato === 'sospesa'" type="error" variant="tonal" class="mb-4"
                    icon="mdi-cancel">
                    <strong>Abbonamento sospeso.</strong>
                    L'accesso è limitato. Contatta il supporto per riattivare il servizio.
                </v-alert>
                <v-alert v-else-if="abbonamento.stato === 'prova'"
                    :type="abbonamento.giorniAllaScadenza <= 5 ? 'error' : 'info'" variant="tonal" class="mb-4"
                    icon="mdi-timer-sand">
                    <div class="d-flex align-center justify-space-between flex-wrap gap-3">
                        <span>
                            <strong>Sei in prova gratuita.</strong>
                            Scade il {{ formatDate(abbonamento.dataScadenza) }}
                            ({{ abbonamento.giorniAllaScadenza }} giorni). Al termine, se non hai attivato
                            l'abbonamento, l'accesso verrà sospeso.
                        </span>
                        <v-btn v-if="!abbonamento.richiestaPendente" size="small" variant="elevated" color="primary"
                            prepend-icon="mdi-credit-card-outline" @click="apriDialogRichiesta('rinnovo')">
                            Procedi al pagamento
                        </v-btn>
                    </div>
                </v-alert>
                <v-alert v-else-if="abbonamento.giorniAllaScadenza <= 30 && abbonamento.stato === 'attiva'"
                    :type="abbonamento.giorniAllaScadenza <= 7 ? 'error' : 'warning'" variant="tonal" class="mb-4"
                    icon="mdi-alarm">
                    <div class="d-flex align-center justify-space-between flex-wrap gap-3">
                        <span>
                            <strong>Abbonamento in scadenza!</strong>
                            Scade il {{ formatDate(abbonamento.dataScadenza) }}
                            ({{ abbonamento.giorniAllaScadenza }} giorni).
                        </span>
                        <v-btn v-if="!abbonamento.richiestaPendente" size="small" variant="elevated" color="primary"
                            prepend-icon="mdi-credit-card-outline" @click="apriDialogRichiesta('rinnovo')">
                            Procedi al pagamento
                        </v-btn>
                    </div>
                </v-alert>

                <!-- Banner richiesta pendente -->
                <v-alert v-if="abbonamento.richiestaPendente" type="info" variant="tonal" class="mb-4"
                    icon="mdi-email-outline">
                    <div class="d-flex align-center justify-space-between flex-wrap gap-2">
                        <span>
                            <strong>Richiesta in attesa</strong> —
                            {{ labelTipoRichiesta(abbonamento.richiestaPendente.tipoRichiesta) }}
                            inviata il {{ formatDate(abbonamento.richiestaPendente.dataRichiesta) }}.
                        </span>
                    </div>
                </v-alert>

                <!-- ── Riepilogo piano attuale ── -->
                <v-row class="mb-4">
                    <v-col cols="12" md="6">
                        <v-card class="piano-card elevation-1" rounded="lg"
                            :class="abbonamento.tipoLicenza === 'pro' ? 'piano-pro' : 'piano-basic'">
                            <v-card-text class="pa-4">
                                <div class="d-flex align-center mb-3">
                                    <v-icon size="20" class="me-2"
                                        :color="abbonamento.tipoLicenza === 'pro' ? 'primary' : 'secondary'">
                                        {{ abbonamento.tipoLicenza === 'pro' ? 'mdi-crown' : 'mdi-shield-check' }}
                                    </v-icon>
                                    <span class="text-h6 font-weight-bold text-uppercase">
                                        Piano {{ abbonamento.tipoLicenza }}
                                    </span>
                                    <v-chip class="ms-2" size="x-small" :color="chipStatoColore(abbonamento.stato)"
                                        variant="tonal">
                                        {{ chipStatoLabel(abbonamento.stato) }}
                                    </v-chip>
                                </div>

                                <div class="info-grid">
                                    <div class="info-row">
                                        <span class="info-label">Utenti inclusi</span>
                                        <span class="info-value">{{ abbonamento.maxUtentiBase }}</span>
                                    </div>
                                    <div class="info-row" v-if="abbonamento.utentiExtra > 0">
                                        <span class="info-label">Utenti extra</span>
                                        <span class="info-value">+ {{ abbonamento.utentiExtra }}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Utenti attivi</span>
                                        <span class="info-value">
                                            {{ abbonamento.utentiAttuali }} / {{ abbonamento.maxUtenti }}
                                        </span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Attivo dal</span>
                                        <span class="info-value">{{ formatDate(abbonamento.dataInizio) }}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Scadenza</span>
                                        <span class="info-value"
                                            :class="abbonamento.giorniAllaScadenza <= 30 ? 'text-error font-weight-bold' : ''">
                                            {{ formatDate(abbonamento.dataScadenza) }}
                                        </span>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- ── Riepilogo prezzi ── -->
                    <v-col cols="12" md="6">
                        <v-card class="prezzi-card elevation-1" rounded="lg">
                            <v-card-text class="pa-4">
                                <div class="text-body-2 font-weight-semibold mb-3 text-medium-emphasis">
                                    RIEPILOGO COSTI ANNUI
                                </div>

                                <div class="costo-row">
                                    <span>Piano {{ abbonamento.tipoLicenza }}</span>
                                    <span>€ {{ abbonamento.prezzoBase.toFixed(2) }}</span>
                                </div>
                                <div class="costo-row" v-if="abbonamento.utentiExtra > 0">
                                    <span>{{ abbonamento.utentiExtra }} utenti extra
                                        (€ {{ EXTRA_ANNUO }}/cad.)</span>
                                    <span>€ {{ abbonamento.prezzoExtra.toFixed(2) }}</span>
                                </div>
                                <v-divider class="my-2" />
                                <div class="costo-row costo-totale">
                                    <span>Totale annuo</span>
                                    <span>€ {{ abbonamento.prezzoTotaleAnnuo.toFixed(2) }}</span>
                                </div>

                                <div class="text-caption text-medium-emphasis mt-2">
                                    I pagamenti avvengono tramite bonifico bancario.
                                    Contattaci per ricevere i dettagli per il pagamento.
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- ── Azioni disponibili ── -->
                <div class="azioni-section mb-4"
                    v-if="!abbonamento.richiestaPendente && abbonamento.stato === 'attiva'">
                    <div class="text-body-2 font-weight-semibold text-medium-emphasis mb-2">MODIFICA PIANO</div>
                    <div class="azioni-grid">

                        <!-- Upgrade/Downgrade piano -->
                        <v-btn v-if="abbonamento.tipoLicenza === 'basic'" variant="tonal" color="primary"
                            prepend-icon="mdi-arrow-up-circle-outline" @click="apriDialogRichiesta('upgrade')">
                            Passa a Pro (€ 28/mese)
                        </v-btn>
                        <v-btn v-else variant="tonal" color="secondary" prepend-icon="mdi-arrow-down-circle-outline"
                            @click="apriDialogRichiesta('downgrade')">
                            Passa a Basic (€ 15/mese)
                        </v-btn>

                        <!-- Aggiungi utenti extra -->
                        <v-btn variant="tonal" color="success" prepend-icon="mdi-account-plus-outline"
                            @click="apriDialogRichiesta('extra_utenti')">
                            Aggiungi utenti (€ 4/utente/mese)
                        </v-btn>

                        <!-- Rimuovi utenti extra -->
                        <v-btn v-if="abbonamento.utentiExtra > 0" variant="tonal" color="warning"
                            prepend-icon="mdi-account-minus-outline" @click="apriDialogRichiesta('rimozione_extra')">
                            Riduci utenti extra
                        </v-btn>

                    </div>
                </div>

                <!-- ── Storico richieste ── -->
                <div class="storico-section">
                    <div class="d-flex align-center mb-2">
                        <span class="text-body-2 font-weight-semibold text-medium-emphasis">
                            STORICO RICHIESTE
                        </span>
                        <v-btn icon size="x-small" variant="text" class="ms-2" @click="caricaDati">
                            <v-icon size="16">mdi-refresh</v-icon>
                        </v-btn>
                    </div>

                    <div v-if="richieste.length === 0" class="text-caption text-grey text-center py-3">
                        Nessuna richiesta inviata
                    </div>

                    <v-table v-else density="compact" class="storico-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Tipo</th>
                                <th>Piano</th>
                                <th>Utenti extra</th>
                                <th>Stato</th>
                                <th>Note operatore</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="r in richieste" :key="r.richiestaId">
                                <td>{{ formatDate(r.dataRichiesta) }}</td>
                                <td>{{ labelTipoRichiesta(r.tipoRichiesta) }}</td>
                                <td>{{ r.pianoRichiesto || '—' }}</td>
                                <td>{{ r.utentiExtraRichiesti ?? '—' }}</td>
                                <td>
                                    <v-chip size="x-small" :color="chipRichiestaColore(r.stato)" variant="tonal">
                                        {{ chipRichiestaLabel(r.stato) }}
                                    </v-chip>
                                </td>
                                <td class="text-caption text-medium-emphasis">{{ r.noteOperatore || '—' }}</td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>

            </template>
        </v-card-text>
    </v-card>

    <!-- ════════════════════════════════════════════════════════════════
         DIALOG: Invia richiesta modifica piano
    ════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="dialogRichiesta" max-width="560" persistent>
        <v-card rounded="lg">
            <v-card-title class="dialog-header pa-4 pb-2 d-flex align-center">
                <v-icon size="18" class="me-2" color="primary">mdi-send-outline</v-icon>
                <span class="text-body-1 font-weight-semibold">{{ titoloDialog }}</span>
                <v-spacer />
                <v-btn icon size="small" variant="text" @click="dialogRichiesta = false">
                    <v-icon size="18">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text class="pa-4">

                <!-- Nuovo abbonamento / rinnovo dopo la prova: selezione piano -->
                <template v-if="tipoRichiestaSel === 'nuovo' || tipoRichiestaSel === 'rinnovo'">
                    <div class="text-body-2 mb-3">
                        Scegli il piano più adatto alla tua azienda. Riceverai subito una email con i dati per
                        il pagamento; l'attivazione avverrà dopo la verifica del bonifico.
                    </div>

                    <v-row dense class="mb-3">
                        <v-col cols="12" sm="6">
                            <v-card class="piano-select-card" variant="outlined" rounded="lg"
                                :class="{ 'piano-select-card--attivo': pianoSelezionato === 'basic' }"
                                @click="pianoSelezionato = 'basic'">
                                <v-card-text class="pa-3">
                                    <div class="d-flex align-center mb-1">
                                        <v-icon size="18" class="me-2" color="secondary">mdi-shield-check</v-icon>
                                        <span class="font-weight-bold">Basic</span>
                                        <v-icon v-if="pianoSelezionato === 'basic'" class="ms-auto" color="primary"
                                            size="20">mdi-check-circle</v-icon>
                                    </div>
                                    <div class="text-h6 font-weight-bold">
                                        € 15<span class="text-caption font-weight-regular">/mese</span>
                                    </div>
                                    <div class="text-caption text-medium-emphasis">Fino a 3 utenti inclusi</div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-card class="piano-select-card" variant="outlined" rounded="lg"
                                :class="{ 'piano-select-card--attivo': pianoSelezionato === 'pro' }"
                                @click="pianoSelezionato = 'pro'">
                                <v-card-text class="pa-3">
                                    <div class="d-flex align-center mb-1">
                                        <v-icon size="18" class="me-2" color="primary">mdi-crown</v-icon>
                                        <span class="font-weight-bold">Pro</span>
                                        <v-icon v-if="pianoSelezionato === 'pro'" class="ms-auto" color="primary"
                                            size="20">mdi-check-circle</v-icon>
                                    </div>
                                    <div class="text-h6 font-weight-bold">
                                        € 28<span class="text-caption font-weight-regular">/mese</span>
                                    </div>
                                    <div class="text-caption text-medium-emphasis">Fino a 10 utenti inclusi</div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-text-field v-model.number="utentiExtraNuovi" label="Utenti extra (opzionale)" type="number"
                        :min="0" variant="outlined" density="compact" hint="€ 4/utente/mese" persistent-hint />

                    <div class="text-body-2 font-weight-semibold mt-3">
                        Totale stimato: € {{ totaleNuovoAbbonamento.toFixed(2) }}/anno
                    </div>
                </template>

                <!-- Upgrade/Downgrade -->
                <template v-if="tipoRichiestaSel === 'upgrade' || tipoRichiestaSel === 'downgrade'">
                    <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                        Stai richiedendo di passare al piano
                        <strong>{{ pianoTarget }}</strong>.
                        La modifica sarà applicata dopo la verifica del pagamento.
                    </v-alert>

                    <!-- Pro-rata calcolato -->
                    <v-card v-if="prorata" class="prorata-card mb-3 elevation-0" color="grey-lighten-4" rounded="lg">
                        <v-card-text class="pa-3">
                            <div class="text-body-2 font-weight-semibold mb-2">
                                💰 Importo pro-rata stimato
                            </div>
                            <div class="prorata-grid">
                                <div>Piano attuale annuo</div>
                                <div>€ {{ prorata.prezzoVecchio.toFixed(2) }}</div>
                                <div>Piano nuovo annuo</div>
                                <div>€ {{ prorata.prezzoNuovo.toFixed(2) }}</div>
                                <div>Giorni rimanenti</div>
                                <div>{{ prorata.giorniRimanenti }} / {{ prorata.giorniTotali }}</div>
                            </div>
                            <v-divider class="my-2" />
                            <div class="d-flex justify-space-between font-weight-bold">
                                <span>{{ tipoRichiestaSel === 'upgrade' ? 'Da pagare' : 'Non rimborsabile' }}</span>
                                <span :class="tipoRichiestaSel === 'upgrade' ? 'text-error' : 'text-success'">
                                    € {{ Math.abs(prorata.importoProrata).toFixed(2) }}
                                </span>
                            </div>
                            <div class="text-caption text-medium-emphasis mt-1">
                                Stima indicativa. L'importo esatto sarà comunicato dall'operatore.
                            </div>
                        </v-card-text>
                    </v-card>
                </template>

                <!-- Utenti extra -->
                <template v-if="tipoRichiestaSel === 'extra_utenti'">
                    <div class="text-body-2 mb-3">
                        Ogni utente aggiuntivo costa <strong>€ 4/mese</strong>
                        (€ {{ EXTRA_ANNUO }}/anno).
                    </div>
                    <v-row dense align="center">
                        <v-col cols="12" sm="6">
                            <div class="text-body-2 text-medium-emphasis mb-1">Utenti extra attuali</div>
                            <div class="text-h6 font-weight-bold">{{ abbonamento?.utentiExtra ?? 0 }}</div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model.number="nuoviUtentiExtra" label="Totale utenti extra richiesti"
                                type="number" :min="(abbonamento?.utentiExtra ?? 0) + 1" variant="outlined"
                                density="compact" hide-details />
                        </v-col>
                    </v-row>
                    <div class="text-caption text-medium-emphasis mt-2">
                        Costo aggiuntivo stimato: € {{ costoExtraAggiuntivo.toFixed(2) }}/anno
                    </div>
                </template>

                <!-- Rimozione utenti extra -->
                <template v-if="tipoRichiestaSel === 'rimozione_extra'">
                    <v-alert type="warning" variant="tonal" density="compact" class="mb-3">
                        La riduzione degli utenti extra avrà effetto dal prossimo rinnovo.
                        L'importo già pagato <strong>non sarà rimborsato</strong>.
                    </v-alert>
                    <v-row dense align="center">
                        <v-col cols="12" sm="6">
                            <div class="text-body-2 text-medium-emphasis mb-1">Utenti extra attuali</div>
                            <div class="text-h6 font-weight-bold">{{ abbonamento?.utentiExtra ?? 0 }}</div>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model.number="nuoviUtentiExtra" label="Nuovo numero utenti extra"
                                type="number" :min="0" :max="(abbonamento?.utentiExtra ?? 1) - 1" variant="outlined"
                                density="compact" hide-details />
                        </v-col>
                    </v-row>
                    <div class="text-caption text-success mt-2" v-if="abbonamento">
                        Risparmio al rinnovo: € {{ risparmioRimozione.toFixed(2) }}/anno
                    </div>
                </template>

                <!-- Note aggiuntive (sempre) -->
                <v-textarea v-model="noteCliente" label="Note aggiuntive (opzionale)" variant="outlined"
                    density="compact" :rows="3" autoGrow :max-rows="6" class="mt-4" />

            </v-card-text>

            <v-divider />
            <v-card-actions class="pa-3">
                <v-spacer />
                <v-btn variant="text" @click="dialogRichiesta = false">Annulla</v-btn>
                <v-btn color="primary" variant="elevated" :loading="loadingRichiesta" @click="inviaRichiesta"
                    prepend-icon="mdi-send">
                    Invia richiesta
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Snackbar feedback -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" :timeout="4000">
        {{ snackbar.text }}
    </v-snackbar>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import {
    get_mio_abbonamento,
    get_mie_richieste,
    invia_richiesta,
    calcola_prorata_cliente
} from '@/api/api.abbonamento.js'

const EXTRA_ANNUO = 48   // 4€/mese * 12

export default {
    name: 'TabLicenzaFatturazione',

    setup() {
        const loading = ref(false)
        const abbonamento = ref(null)
        const richieste = ref([])

        // ── Dialog richiesta ──────────────────────────────────────────────────
        const dialogRichiesta = ref(false)
        const tipoRichiestaSel = ref(null)
        const noteCliente = ref('')
        const nuoviUtentiExtra = ref(0)
        const loadingRichiesta = ref(false)
        const prorata = ref(null)

        // ── Nuovo abbonamento: piano scelto dall'utente ────────────────────────
        const pianoSelezionato = ref('basic')
        const utentiExtraNuovi = ref(0)
        const PREZZI_BASE = { basic: 180, pro: 336 }

        const snackbar = ref({ show: false, text: '', color: 'success' })

        // ── Computed ──────────────────────────────────────────────────────────

        const pianoTarget = computed(() => {
            if (tipoRichiestaSel.value === 'upgrade') return 'Pro'
            if (tipoRichiestaSel.value === 'downgrade') return 'Basic'
            return ''
        })

        const titoloDialog = computed(() => {
            const map = {
                nuovo: 'Richiedi abbonamento',
                rinnovo: 'Procedi al pagamento',
                upgrade: 'Upgrade a Piano Pro',
                downgrade: 'Downgrade a Piano Basic',
                extra_utenti: 'Aggiungi utenti extra',
                rimozione_extra: 'Riduci utenti extra',
            }
            return map[tipoRichiestaSel.value] || 'Richiesta'
        })

        const costoExtraAggiuntivo = computed(() => {
            if (!abbonamento.value) return 0
            const diff = (nuoviUtentiExtra.value || 0) - (abbonamento.value.utentiExtra || 0)
            return Math.max(0, diff) * EXTRA_ANNUO
        })

        const risparmioRimozione = computed(() => {
            if (!abbonamento.value) return 0
            const diff = (abbonamento.value.utentiExtra || 0) - (nuoviUtentiExtra.value || 0)
            return Math.max(0, diff) * EXTRA_ANNUO
        })

        const totaleNuovoAbbonamento = computed(() => {
            const base = PREZZI_BASE[pianoSelezionato.value] ?? PREZZI_BASE.basic
            const extra = (utentiExtraNuovi.value || 0) * EXTRA_ANNUO
            return base + extra
        })

        // ── Caricamento ───────────────────────────────────────────────────────

        const caricaDati = async () => {
            loading.value = true
            try {
                const [resAbb, resRich] = await Promise.all([
                    get_mio_abbonamento(),
                    get_mie_richieste()
                ])
                abbonamento.value = resAbb.data
                richieste.value = resRich.data || []
            } finally {
                loading.value = false
            }
        }

        // ── Dialog ────────────────────────────────────────────────────────────

        const apriDialogRichiesta = async (tipo) => {
            tipoRichiestaSel.value = tipo
            noteCliente.value = ''
            prorata.value = null
            nuoviUtentiExtra.value = abbonamento.value?.utentiExtra ?? 0
            pianoSelezionato.value = abbonamento.value?.tipoLicenza ?? 'basic'
            utentiExtraNuovi.value = abbonamento.value?.utentiExtra ?? 0

            // Calcola pro-rata per upgrade/downgrade
            if ((tipo === 'upgrade' || tipo === 'downgrade') && abbonamento.value) {
                const pianoNuovo = tipo === 'upgrade' ? 'pro' : 'basic'
                try {
                    const res = await calcola_prorata_cliente(pianoNuovo, abbonamento.value.utentiExtra)
                    prorata.value = res.data
                } catch { /* ignora, mostriamo senza prorata */ }
            }

            dialogRichiesta.value = true
        }

        const inviaRichiesta = async () => {
            loadingRichiesta.value = true
            try {
                let pianoRichiesto = null
                let extraRichiesti = null

                if (tipoRichiestaSel.value === 'upgrade') {
                    pianoRichiesto = 'pro'
                    extraRichiesti = abbonamento.value?.utentiExtra ?? 0
                } else if (tipoRichiestaSel.value === 'downgrade') {
                    pianoRichiesto = 'basic'
                    extraRichiesti = abbonamento.value?.utentiExtra ?? 0
                } else if (tipoRichiestaSel.value === 'nuovo' || tipoRichiestaSel.value === 'rinnovo') {
                    pianoRichiesto = pianoSelezionato.value
                    extraRichiesti = utentiExtraNuovi.value || 0
                } else if (tipoRichiestaSel.value === 'extra_utenti') {
                    extraRichiesti = nuoviUtentiExtra.value
                } else if (tipoRichiestaSel.value === 'rimozione_extra') {
                    extraRichiesti = nuoviUtentiExtra.value
                }

                await invia_richiesta({
                    tipoRichiesta: tipoRichiestaSel.value,
                    pianoRichiesto,
                    utentiExtraRichiesti: extraRichiesti,
                    noteCliente: noteCliente.value
                })

                dialogRichiesta.value = false
                notifica('Richiesta inviata con successo. Riceverai una conferma via email.')
                await caricaDati()
            } finally {
                loadingRichiesta.value = false
            }
        }

        // ── Helpers display ───────────────────────────────────────────────────

        const formatDate = (d) => {
            if (!d) return '—'
            return new Date(d).toLocaleDateString('it-IT')
        }

        const labelTipoRichiesta = (tipo) => {
            const map = {
                nuovo: 'Nuovo abbonamento',
                rinnovo: 'Conferma pagamento',
                upgrade: 'Upgrade piano',
                downgrade: 'Downgrade piano',
                extra_utenti: 'Utenti extra',
                rimozione_extra: 'Rimozione utenti extra',
            }
            return map[tipo] || tipo
        }

        const chipStatoColore = (stato) => {
            const map = {
                attiva: 'success',
                attesa_pagamento: 'warning',
                sospesa: 'error',
                scaduta: 'grey',
            }
            return map[stato] || 'grey'
        }

        const chipStatoLabel = (stato) => {
            const map = {
                attiva: 'Attiva',
                attesa_pagamento: 'In attesa',
                sospesa: 'Sospesa',
                scaduta: 'Scaduta',
            }
            return map[stato] || stato
        }

        const chipRichiestaColore = (stato) => {
            const map = { in_attesa: 'warning', approvata: 'success', rifiutata: 'error' }
            return map[stato] || 'grey'
        }

        const chipRichiestaLabel = (stato) => {
            const map = { in_attesa: 'In attesa', approvata: 'Approvata', rifiutata: 'Rifiutata' }
            return map[stato] || stato
        }

        const notifica = (text, color = 'success') => {
            snackbar.value = { show: true, text, color }
        }

        onMounted(caricaDati)

        return {
            EXTRA_ANNUO,
            loading, abbonamento, richieste,
            dialogRichiesta, tipoRichiestaSel, noteCliente,
            nuoviUtentiExtra, loadingRichiesta, prorata,
            pianoSelezionato, utentiExtraNuovi, totaleNuovoAbbonamento,
            pianoTarget, titoloDialog,
            costoExtraAggiuntivo, risparmioRimozione,
            snackbar,
            caricaDati,
            apriDialogRichiesta, inviaRichiesta,
            formatDate, labelTipoRichiesta,
            chipStatoColore, chipStatoLabel,
            chipRichiestaColore, chipRichiestaLabel,
        }
    }
}
</script>

<style scoped>
.empty-abb {
    text-align: center;
    padding: 48px 16px;
    color: #6b7280;
}

/* Piano card */
.piano-card {
    border: 2px solid transparent;
}

.piano-pro {
    border-color: rgba(30, 58, 95, .25);
}

.piano-basic {
    border-color: rgba(245, 158, 11, .25);
}

/* Selezione piano nuovo abbonamento */
.piano-select-card {
    cursor: pointer;
    transition: border-color .15s, box-shadow .15s;
}

.piano-select-card:hover {
    border-color: rgba(30, 58, 95, .35);
}

.piano-select-card--attivo {
    border-color: #1E3A5F !important;
    border-width: 2px;
    box-shadow: 0 2px 8px rgba(30, 58, 95, .12);
}

.info-grid {
    display: grid;
    gap: 8px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
}

.info-label {
    color: #6b7280;
}

.info-value {
    font-weight: 600;
    color: #1E3A5F;
}

/* Prezzi */
.prezzi-card {
    background: #fafbfc;
    border: 1px solid #eef0f3;
}

.costo-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    margin-bottom: 6px;
}

.costo-totale {
    font-size: 15px;
    font-weight: 700;
    color: #1E3A5F;
}

/* Azioni */
.azioni-section {
    background: #f9fafb;
    border-radius: 10px;
    padding: 14px;
}

.azioni-grid {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

/* Storico */
.storico-section {
    background: #f9fafb;
    border-radius: 10px;
    padding: 14px;
}

.storico-table :deep(th) {
    font-size: 11px !important;
    text-transform: uppercase;
    letter-spacing: .5px;
    color: #6b7280;
}

.storico-table :deep(td) {
    font-size: 12px;
}

/* Dialog header */
.dialog-header {
    background: #f7f7f8;
    border-radius: 12px 12px 0 0;
}

/* Pro-rata */
.prorata-card {
    border: 1px solid #e5e7eb;
}

.prorata-grid {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 4px 16px;
    font-size: 13px;
}
</style>